import React, { useId, useState } from 'react';
import { ChevronDown, Info } from 'lucide-react';

import { cn } from '@epoch-ui/lib/utils';
import { Banner, BannerIcon } from '@epoch-ui/components/Banner';
import { Badge } from '@epoch-ui/components/Badge/badge';
import { Button } from '@epoch-ui/components/Button/button';
import { formatNumberWithComma } from 'utils/format';

interface SkippedGuestsBannerProps {
  emails: string[];
  dedupeRequired: boolean;
  helpUrl: string;
}

/**
 * Lists emails the backend skipped while inviting distrolists or communities: an email
 * held by more than one active account, or an email whose guest row belongs to a
 * different account.
 * Guests already on the event are not listed. Collapsed by default; the chevron shows
 * the chips.
 */
export const SkippedGuestsBanner: React.FC<SkippedGuestsBannerProps> = ({
  emails,
  dedupeRequired,
  helpUrl
}) => {
  const [expanded, setExpanded] = useState(false);
  const listId = useId();

  if (!dedupeRequired && emails.length === 0) {
    return null;
  }

  const hasEmails = emails.length > 0;

  return (
    <Banner variant="secondary" role="status" className="tw-flex-col tw-items-stretch tw-gap-2">
      <div className="tw-flex tw-items-center tw-gap-2">
        <BannerIcon icon={Info} />
        <p className="tw-m-0 tw-text-sm">
          {hasEmails
            ? `Some guests (${formatNumberWithComma(emails.length)}) will not be invited ` +
              'because their email matches multiple active accounts or an event guest ' +
              'record for a different account. Invite them by hand.'
            : 'Duplicate guests were removed.'}{' '}
          <a
            href={helpUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="tw-text-primary tw-underline"
          >
            Learn more
          </a>
        </p>
        {hasEmails && (
          <Button
            variant="ghost"
            size="none"
            className="tw-ml-auto tw-p-1 hover:tw-bg-transparent focus:tw-ring-offset-0 focus-visible:tw-ring-offset-0"
            aria-expanded={expanded}
            aria-controls={listId}
            aria-label={expanded ? 'Hide duplicate guests' : 'Show duplicate guests'}
            onClick={() => setExpanded((prev) => !prev)}
          >
            <ChevronDown
              className={cn(
                'tw-size-4 tw-transition-transform tw-duration-200',
                expanded && 'tw-rotate-180'
              )}
            />
          </Button>
        )}
      </div>

      {hasEmails && expanded && (
        <ul
          id={listId}
          className="tw-m-0 tw-flex tw-max-h-[124px] tw-list-none tw-flex-wrap tw-gap-1 tw-overflow-y-auto tw-p-0 tw-duration-200 tw-animate-in tw-fade-in-0 tw-slide-in-from-top-1"
        >
          {emails.map((email) => (
            <li key={email}>
              <Badge variant="outline" size="sm" className="tw-bg-background">
                {email}
              </Badge>
            </li>
          ))}
        </ul>
      )}
    </Banner>
  );
};
